import React, { forwardRef, useRef, useState, useEffect, type ChangeEvent } from 'react';
import { View, ViewOff } from '@core/icons-react';
import { InputWrapper } from '../shared/InputWrapper/InputWrapper';
import { useNormalizedInputProps } from '../shared/hooks/useNormalizedInputProps';
import { textInputProps } from '../shared/utils/textInputProps';
import type { BaseInputProps } from '../shared/types/input.types';

export interface SensitiveInputProps extends Omit<BaseInputProps<HTMLInputElement>, 'onChange'> {
  /**
   * Optionally provide an `onChange` handler that is called whenever input is updated
   */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  
  /**
   * Specify an optional value for the `autocomplete` property on the underlying `<input>`
   */
  autoComplete?: string;
  
  /**
   * Provide custom text for the password visibility toggle button when password is hidden
   */
  showPasswordLabel?: string;
  
  /**
   * Provide custom text for the password visibility toggle button when password is visible
   */
  hidePasswordLabel?: string;
  
  /**
   * Specify whether the password visibility toggle should be shown
   */
  showPasswordToggle?: boolean;
  
  /**
   * Whether the password is initially visible
   */
  defaultVisible?: boolean;
  
  /**
   * Callback when password visibility changes
   */
  onVisibilityChange?: (visible: boolean) => void;
}

export const SensitiveInput = forwardRef<HTMLInputElement, SensitiveInputProps>(function SensitiveInput(
  {
    className,
    inputClassName,
    disabled = false,
    enableCounter = false,
    helperText,
    hideLabel,
    hidePasswordLabel = 'Hide password',
    id,
    inline = false,
    invalid = false,
    invalidText,
    labelText,
    maxCount,
    onChange = () => {},
    onClick = () => {},
    onFocus,
    onBlur,
    onKeyDown,
    onVisibilityChange,
    placeholder,
    readOnly,
    required,
    showPasswordLabel = 'Show password',
    showPasswordToggle = true,
    size = 'md',
    value,
    defaultValue,
    defaultVisible = false,
    warn = false,
    warnText,
    autoComplete = 'current-password',
    ...rest
  },
  ref
) {
  const prefix = 'core';
  const inputRef = useRef<HTMLInputElement>(null);
  const mergedRef = ref || inputRef;
  const [isPasswordVisible, setIsPasswordVisible] = useState(defaultVisible);
  
  // Track text count for counter feature
  const getInitialTextCount = (): number => {
    const targetValue = defaultValue || value || inputRef.current?.value || '';
    return targetValue.toString().length;
  };
  
  const [textCount, setTextCount] = useState(getInitialTextCount());
  
  useEffect(() => {
    setTextCount(getInitialTextCount());
  }, [value, defaultValue, enableCounter]);
  
  const normalizedProps = useNormalizedInputProps({
    id,
    readOnly,
    disabled,
    invalid,
    invalidText,
    warn,
    warnText,
  });
  
  const togglePasswordVisibility = () => {
    if (!disabled && !readOnly) {
      const newVisibility = !isPasswordVisible;
      setIsPasswordVisible(newVisibility);
      if (onVisibilityChange) {
        onVisibilityChange(newVisibility);
      }
    }
  };
  
  const sensitiveInputClasses = [
    `${prefix}-sensitive-input__input`,
    `${prefix}-text-input`,
    normalizedProps.invalid && `${prefix}-text-input--invalid`,
    normalizedProps.warn && `${prefix}-text-input--warning`,
    size && `${prefix}-text-input--${size}`,
    showPasswordToggle && `${prefix}-sensitive-input__input--with-toggle`,
    inputClassName,
  ].filter(Boolean).join(' ');
  
  const sharedSensitiveInputProps: any = {
    id,
    onChange: (evt: ChangeEvent<HTMLInputElement>) => {
      if (!normalizedProps.disabled) {
        setTextCount(evt.target.value?.length || 0);
        onChange(evt);
      }
    },
    onClick: (evt: React.MouseEvent<HTMLInputElement>) => {
      if (!normalizedProps.disabled) {
        onClick(evt);
      }
    },
    onFocus,
    onBlur,
    onKeyDown,
    placeholder,
    type: isPasswordVisible ? 'text' : 'password',
    ref: mergedRef,
    className: sensitiveInputClasses,
    title: placeholder,
    disabled: normalizedProps.disabled,
    readOnly,
    required,
    value,
    defaultValue,
    autoComplete,
    'aria-describedby': helperText ? normalizedProps.helperId : undefined,
    'data-invalid': normalizedProps.invalid || undefined,
    ...rest,
  };
  
  // Add maxLength if counter is enabled
  if (enableCounter && maxCount) {
    sharedSensitiveInputProps.maxLength = maxCount;
  }
  
  const input = (
    <div className={`${prefix}-sensitive-input__controls`}>
      <input
        {...textInputProps({
          sharedTextInputProps: sharedSensitiveInputProps,
          invalid: normalizedProps.invalid,
          invalidId: normalizedProps.invalidId,
          warn: normalizedProps.warn,
          warnId: normalizedProps.warnId,
        })}
      />
      {showPasswordToggle && (
        <button
          type="button"
          className={`${prefix}-sensitive-input__toggle`}
          disabled={disabled || readOnly}
          onClick={togglePasswordVisibility}
          aria-label={isPasswordVisible ? hidePasswordLabel : showPasswordLabel}
          title={isPasswordVisible ? hidePasswordLabel : showPasswordLabel}
        >
          {isPasswordVisible ? (
            <ViewOff size={16} />
          ) : (
            <View size={16} />
          )}
        </button>
      )}
    </div>
  );
  
  const sensitiveInputWrapperClasses = [
    `${prefix}-sensitive-input-wrapper`,
    className,
  ].filter(Boolean).join(' ');
  
  return (
    <InputWrapper
      className={sensitiveInputWrapperClasses}
      disabled={normalizedProps.disabled}
      enableCounter={enableCounter}
      helperText={helperText}
      hideLabel={hideLabel}
      id={id}
      inline={inline}
      invalid={normalizedProps.invalid}
      invalidText={invalidText}
      labelText={labelText}
      maxCount={maxCount}
      readOnly={readOnly}
      required={required}
      size={size}
      textCount={textCount}
      warn={normalizedProps.warn}
      warnText={warnText}
    >
      {input}
    </InputWrapper>
  );
});

SensitiveInput.displayName = 'SensitiveInput';
